import { ITokenPitchDetails, IPitch } from '@appInterfaces/pitch.interfaces';
import {
  TPortfolioCompanyEquity,
  TPortfolioPitchToken,
  IPortfolioCompany,
  IPortfolioReducer,
  TPortfolioSpv,
} from '@portfolio/IPortfolioReducer';
import { ICompanyEquity, ICompany } from '@appInterfaces/company.interfaces';
import { serverPagination } from '@appInterfaces/general.interfaces';
import { ISPVBalance, ISPV, TAsset } from '@appInterfaces/spv.interfaces';
import { EPitchSpecialization } from '@enums/EPitchSpecialization';

interface initialCompany {
  id: number;
  equity?: TPortfolioCompanyEquity | null;
  tokenPitch?: TPortfolioPitchToken | null;
}

export const mapPitchesToTokenPitches = (
  state: IPortfolioReducer,
  pitches: IPitch[]
) => {
  const tokenPitches = pitches.filter(
    pitch => pitch.specialisation_type === EPitchSpecialization.tokenPitch
  );

  if (tokenPitches.length) {
    return state.companies.map((company) => {
      const pitch = tokenPitches.find(pitch => pitch.company_id === company.id);
      if (pitch && company.tokenPitch) {
        return {
          ...company,
          tokenPitch: {
            ...company.tokenPitch,
            ...setTokenPitchDetails(pitch),
          },
        };
      }
      return company;
    });
  }

  return state.companies;
};

export const mapSpvToStore = (
  state: IPortfolioReducer,
  data: {
    spvs: ISPV[];
    assets: ISPVBalance[];
  }
) => {
  const updatedSpv = data.spvs.map((spv) => {
    const spvData = setSpv(spv);
    const spvAssets = spvData.spv.assets.map((asset: TAsset, index: number) => {

      const assetData = data.assets.find(
        assetBalance => assetBalance.asset_id === asset.id
      );

      return {
        ...spv.assets[index],
        amount: setAssetAmount(assetData),
      };
    });

    return { company_id: spvData.company_id, spv: { ...spvData.spv, assets: spvAssets } };
  });
  return assignSpvToCompany(updatedSpv, state.companies);
};

const assignSpvToCompany = (
  spvData: { company_id: number; spv: TPortfolioSpv }[],
  stateCompanies: IPortfolioCompany[]
) => {
  let companies = [...stateCompanies];

  spvData.find((spv) => {
    companies = companies.map(
      (company: IPortfolioCompany) => {
        if (spv.company_id === company.id) {
          return { ...company, spv: { ...spv.spv } };
        }
        return company;
      }
    );
  });

  return companies;
};

export const mapCompanyDetailsToStore = (
  state: IPortfolioReducer,
  companyDetails: ICompany[]
) => {
  return state.companies.map((company, index) => {
    const details = companyDetails.find(details => details.id === company.id);
    return { ...state.companies[index], details: { ...setCompanyDetails(details!) } };
  });
};

export const mapInitialDataToStore = (
  resp: [ICompanyEquity[], serverPagination<ITokenPitchDetails[]>]
): initialCompany[] => {

  const equities = resp[0];
  const tokens = resp[1].items;

  let companies: initialCompany[] = [];
  if (equities !== null && equities.length !== 0) {
    companies = [...equities.map(equity => setEquity(equity))];
  }

  if (tokens === null) return companies;

  tokens.forEach((token) => {
    if (companies.length !== 0) {
      const companyIndex = companies.findIndex(
        company => company.id === token.company_id
      );

      if (companyIndex !== -1) {
        companies[companyIndex] = {
          ...companies[companyIndex],
          ...setToken(token),
        };
      } else {
        companies = [...companies, { ...setToken(token) }];
      }
    } else {
      // @ts-ignore - ongoing refactor, updating TS
      companies = [{ id: token.company_id,  ...setToken(token), equity: null }];
    }
  });
  return companies;
};

const setSpv = (spv: ISPV): { company_id: number; spv: TPortfolioSpv } => {
  const { id, name, status, assets, company_id } = spv;
  return {
    company_id: company_id,
    spv: {
      id,
      name,
      status,
      assets,
    },
  };
};

const setAssetAmount = (assetData: ISPVBalance | undefined): string =>
  assetData ? assetData.amount : '0';

const setCompanyDetails = (details: ICompany) => {
  const { slug, name, legal_name, avatar_url } = details;
  return { slug, name, legal_name, avatar_url };
};

const setTokenPitchDetails = (pitch: IPitch) => {
  const { id, slug, image_url } = pitch;
  return {
    id,
    slug,
    image_url,
  };
};

const setToken = (
  token: ITokenPitchDetails
): {
  id: number;
  tokenPitch: {
    details: Omit<ITokenPitchDetails, 'id' | 'company_id'>;
  };
} => {
  const { company_id, token_name, token_symbol, security, distributed } = token;

  return {
    id: company_id,
    tokenPitch: {
      details: { token_name, token_symbol, security, distributed },
    },
  };
};

const setEquity = (
  equity: ICompanyEquity
): { id: number; equity: TPortfolioCompanyEquity } => {
  const {
    company_id,
    value,
    profit,
    performance_percent,
    amount_invested,
    total_paid_out,
    acquisition_type,
  } = equity;

  return {
    id: company_id,
    equity: {
      value,
      profit,
      performance_percent,
      amount_invested,
      total_paid_out,
      acquisition_type,
    },
  };
};
